import React from 'react';
import { getTheme, useThemeMode } from '../theme';
import { Switch } from '../components/ui';
import {
  getHuntMediaSyncStatus, isAutoFetchSupported, readHuntMediaPrefs, subscribeHuntMediaSync, syncHuntMedia, writeHuntMediaPrefs,
  type HuntMediaPrefs, type HuntMediaSyncStatus,
} from '../huntMediaSync';

export function useHuntMediaPrefs(): [HuntMediaPrefs, (change: Partial<HuntMediaPrefs>) => void] {
  const [prefs, setPrefs] = React.useState(readHuntMediaPrefs);
  React.useEffect(() => subscribeHuntMediaSync(() => setPrefs(readHuntMediaPrefs())), []);
  return [prefs, change => setPrefs(writeHuntMediaPrefs(change))];
}

export function useHuntMediaSyncStatus(): HuntMediaSyncStatus {
  return React.useSyncExternalStore(subscribeHuntMediaSync, getHuntMediaSyncStatus);
}

export function useAutoFetchSupported(): boolean {
  const [ok, setOk] = React.useState(false);
  React.useEffect(() => { void isAutoFetchSupported().then(setOk); }, []);
  return ok;
}

const fmtWhen = (ms: number) => new Date(ms).toLocaleString('sv-SE', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

/** Raden som förklarar vad den automatiska hämtningen gör just nu. */
export function syncStatusText(s: HuntMediaSyncStatus): string | null {
  const n = s.queue.length;
  const what = `${n} ${n === 1 ? 'bild eller klipp' : 'bilder och klipp'}`;
  switch (s.state) {
    case 'scanning': return 'Letar efter bilder från jakten…';
    case 'uploading': return `Laddar upp ${Math.min(s.done + 1, n)} av ${n}…`;
    case 'waitingForWifi': return n > 0 ? `${what} laddas upp när telefonen är på wifi.` : null;
    case 'noAccess': return 'venari har inte åtkomst till Bilder. Tillåt det under Inställningar → venari → Bilder.';
    default: return s.last ? `Senast hämtat: ${s.last.count} till ${s.last.markName || 'jakten'}, ${fmtWhen(s.last.at)}.` : null;
  }
}

/** Inställningar → Jaktbilder (A3). Visas bara i iPhone-appen med stöd för automatisk hämtning. */
export function HuntMediaSettingsSection({ cardStyle }: { cardStyle: React.CSSProperties }) {
  const T = getTheme(useThemeMode().mode);
  const supported = useAutoFetchSupported();
  const [prefs, setPrefs] = useHuntMediaPrefs();
  const status = useHuntMediaSyncStatus();
  if (!supported) return null;

  const sub = { color: T.textSub, fontSize: 13, lineHeight: 1.5 } as const;
  const on = prefs.auto === 'on';
  const statusText = on ? syncStatusText(status) : null;

  return (
    <div style={{ ...cardStyle, marginBottom: 16 }}>
      <div style={{ color: T.textSub, fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 8 }}>Jaktbilder</div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, padding: '6px 0' }}>
        <div>
          <div style={{ color: T.text, fontSize: 14, fontWeight: 600 }}>Hämta jaktbilder automatiskt</div>
          <div style={sub}>
            Bilder och klipp i Bilder som togs under en jakt, på eller nära marken, laddas upp till jaktens album utan att du väljer.
            Bilderna görs mindre innan de skickas. Annat i Bilder rörs inte.
          </div>
        </div>
        <Switch checked={on} onChange={v => setPrefs({ auto: v ? 'on' : 'off' })} ariaLabel="Hämta jaktbilder automatiskt" />
      </div>
      {on && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, padding: '6px 0', borderTop: `1px solid ${T.border}` }}>
          <div>
            <div style={{ color: T.text, fontSize: 14, fontWeight: 600 }}>Även via mobildata</div>
            <div style={sub}>Annars väntar uppladdningen tills telefonen är på wifi.</div>
          </div>
          <Switch checked={prefs.allowCellular} onChange={v => setPrefs({ allowCellular: v })} ariaLabel="Ladda upp jaktbilder även via mobildata" />
        </div>
      )}
      {statusText && (
        <div style={{ ...sub, marginTop: 8, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ flex: 1, minWidth: 180 }}>{statusText}</span>
          {status.state === 'waitingForWifi' && status.queue.length > 0 && (
            <button type="button" onClick={() => void syncHuntMedia({ force: true })}
              style={{ padding: '6px 12px', borderRadius: 6, fontWeight: 700, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', background: T.bg, color: T.text, border: `1px solid ${T.border}` }}>
              Ladda upp nu
            </button>
          )}
        </div>
      )}
    </div>
  );
}
